import React, { ReactNode, ErrorInfo } from "react";
import { AlertOctagon, RefreshCcw, Home } from "lucide-react";

interface ErrorBoundaryProps {
  children: ReactNode;
  compact?: boolean;
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: undefined });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // [Phase 4] TODO: Replace this with Sentry.captureException or Custom Tracker SDK
    console.error("[MONITORING] Fatal Error Captured:", error, errorInfo);
    // e.g., captureError({ err: error, info: errorInfo, timestamp: new Date() })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={`${this.props.compact ? "min-h-48" : "min-h-screen"} bg-zinc-50 flex items-center justify-center p-4`}>
          <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-zinc-100 p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertOctagon className="w-8 h-8" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-zinc-900">Algo deu errado</h1>
              <p className="text-zinc-500">
                Não foi possível exibir esta página. Você pode tentar novamente ou acessar outra página.
              </p>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-xl transition-colors font-medium shadow-sm"
              >
                <RefreshCcw className="w-4 h-4" />
                Recarregar Página
              </button>
              <button
                onClick={() => window.location.assign("/")}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white hover:bg-zinc-50 text-zinc-700 border border-zinc-200 px-5 py-2.5 rounded-xl transition-colors font-medium"
              >
                <Home className="w-4 h-4" />
                Voltar ao Início
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
